import {defineStore} from "pinia";
import {computed, ref, watch} from "vue";
import * as CoreService from "../api";
import {LocationQueryRaw} from "vue-router";
import * as DateUtils from "@/helpers/dateUtils";
import * as LocationService from "@/services/location.service";
import * as RegionService from "@/services/region.service";
import * as CountryService from "@/services/country.service";
import * as CityService from "@/services/city.service";
import * as CmsService from "@/services/cms.service";
import {useLocalization} from "@/locales/localization";
import {getHeaderAnnouncement} from "@/services/cms.service";
import * as TripService from "@/services/trip.service";


export const useHomepageStore = defineStore('homepageStore', () => {

    const { currentLocale } = useLocalization();
    const status = ref<'idle' | 'loading' | 'success' | 'error'>('idle');
    const current = ref<'none' | 'destination' | 'date' | 'passenger' | 'boat'>('none')
    const categories = ref([])
    const headerAnnouncements = ref<any[]>([]);
    const warning=ref('')
    const popular=ref([])
    const homepageData = ref(null); 
    const heroImageUrl = ref("");
    const selections = ref({
        location: {},
        category: null,
        passengers: 2,
        cabins: 1,
        skipper: false,
        dates: []
    })
    const open = (input) => {
        current.value = input == null || input === '' ? 'none' : input
    }

    const isOpen = computed(() => {
        return current.value != 'none'
    })

    const hasQuery = computed(() => {
        return selections.value && 'entity' in selections.value.location && selections.value.location.entity.name != '';
    })
    const canSearch = computed(() => {
        return (
            hasQuery.value &&
            selections.value.dates.length === 2 &&
            selections.value.dates[0] !== null &&
            selections.value.dates[1] !== null
        );
    });

    const canRecommend = computed(() => {
        return hasQuery.value &&
            selections.value.dates.length === 2 &&
            selections.value.category != null &&
            Object.keys(selections.value.category).length > 0;
    });
    
    watch(canRecommend, (newValue) => {
        console.log("Can recommend:", newValue);
    });

    const query = computed(() => {

        const q: LocationQueryRaw = {};
        if (selections.value.location !== null) {

            //TODO Unify this
            if (selections.value.location.entity === 'Region') {
                q['regionIds'] = selections.value.location.id;
            } else if (selections.value.location.entity === 'Country') {
                q['countryIds'] = selections.value.location.id;
            } else if (selections.value.location.entity=== 'Harbour') {
                q['locationIds'] = selections.value.location.id;
            } else if (selections.value.location.entity === 'Location') {
                q['cityIds'] = selections.value.location.id;
            }else if (selections.value.location.entity === 'location') {
                q['cityIds'] = selections.value.location.value.replace('location:');
            }else if (selections.value.location.entity === 'country') {
                q['countryIds'] = selections.value.location.value.replace('country:','');
            }else if (selections.value.location.entity === 'region') {
                q['regionIds'] = selections.value.location.value.replace('region:','');
            }else if (selections.value.location.entity === 'harbour') {
                q['locationIds'] = selections.value.location.value.replace('harbour:','');
            }
        }

        let dates = DateUtils.format(DateUtils.getNextSaturdays())
        if (selections.value.dates !== null) {
            dates = DateUtils.format(selections.value.dates);

            if(dates.length > 1) {
                dates.push(dates[0]);
            }
        }

        q['dateFrom'] = dates[0];
        q['dateTo'] = dates[1];


        if (selections.value.cabins > 0 ) {
            q['cabins'] =  selections.value.cabins;
        }

        if (selections.value.passengers > 0) {
            q['passengers'] =  selections.value.passengers;
        }

        q['skipper'] = String(selections.value.skipper);

        if (selections.value.category !== null) {
            q['categories'] = selections.value.category.id;
        }

        return q;
    })

    const next = () => {


        if (selections.value.category) {
            current.value = 'passenger'
            return
        }

        if (selections.value.dates && selections.value.dates.length == 2 ) {
            current.value = 'boat'
            return
        }

        if(!hasQuery.value) {
            current.value = 'destination'
            return
        }

        current.value = 'date'
    }


    const getCategories = () => {
        status.value = "loading"
        CoreService.getCategories()
            .then((data) => {
                categories.value = data
            }).finally(() => {
            status.value = "idle"
        })
    }

    async function getHeaderAnnouncement(){
        status.value = "loading"
        try {
            const res = await CmsService.getHeaderAnnouncement(currentLocale.value)
            headerAnnouncements.value = res?.data || [];
        } catch (error) {
            console.error("Header announcement failed:", error);
            throw error;
        }
        status.value = "idle"
    }


    const reset = () => {
        status.value = 'idle';
        current.value = 'none';
        warning.value = '';
        popular.value = [];
        selections.value = {
            location: {},
            category: null,
            passengers: 2,
            cabins: 1,
            skipper: false,
            dates:  []
        };
    }
    async function fetchHomepageData() {
        status.value = "loading";

        try {

            const cachedImageUrl = localStorage.getItem("heroImageUrl");
            if (cachedImageUrl) {
                heroImageUrl.value = cachedImageUrl;
            }
            
            const response = await CmsService.getHomePageContents(currentLocale.value);
            homepageData.value = response.data;

            const newImageUrl = response.data.heroImage?.max || response.data.heroImage?.full;
            
            if (newImageUrl && newImageUrl !== cachedImageUrl) {
                await preloadImage(newImageUrl); 
                heroImageUrl.value = newImageUrl;
                localStorage.setItem("heroImageUrl", newImageUrl);
            }

            status.value = "success";
        } catch (error) {
            console.error("[HomepageStore] Failed to load homepage data:", error);
            status.value = "error";
        }
    }
    
    function preloadImage(url) {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.src = url;
            img.onload = resolve;
            img.onerror = reject;
        });
    }


    watch(() => selections.value.location, (newLocation, oldLocation) => {
        console.log("[HomepageStore] Location changed:", { old: oldLocation, new: newLocation });

        warning.value = '';

        if (!newLocation || Object.keys(newLocation).length === 0) {
            return;
        }
        
        if (newLocation.entity === "Harbour") {
            LocationService.searchByIds([newLocation.id])
                .then((res) => {
                    if (!res || !res.data || res.data._embedded.locations.length <= 0) return;
                    warning.value = res.data._embedded.locations[0]['saleWarning'].trim();
                })
                .catch((err) => console.error("[HomepageStore] Harbour API error:", err));
        }

        if (newLocation.entity === "Country") {
            CountryService.searchByIds([newLocation.id])
                .then((res) => {
                    if (!res || !res.data || res.data._embedded.countries.length <= 0) return;
                    warning.value = res.data._embedded.countries[0]['saleWarning'].trim();
                })
                .catch((err) => console.error("[HomepageStore] Country API error:", err));
        }

        if (newLocation.entity === "Location") {
            CityService.searchByIds([newLocation.id])
                .then((res) => {
                    if (!res || !res.data || res.data._embedded.cities.length <= 0) return;
                    warning.value = res.data._embedded.cities[0]['saleWarning'].trim();
                })
                .catch((err) => console.error("[HomepageStore] City API error:", err));
        }

        if (newLocation.entity === "Region") {
            RegionService.searchByIds([newLocation.id])
                .then((res) => {
                    if (!res || !res.data || res.data._embedded.regions.length <= 0) return;
                    warning.value = res.data._embedded.regions[0]['saleWarning'].trim();
                })
                .catch((err) => console.error("[HomepageStore] Region API error:", err));
        }
    }, { deep: true });

    return {
        isOpen,
        hasQuery,
        status,
        current,
        selections,
        query,
        categories,
        canSearch,
        canRecommend,
        warning,
        headerAnnouncements,
        getHeaderAnnouncement,
        fetchHomepageData,
        heroImageUrl,
        open,
        getCategories,
        next,
        reset,
    };
});